import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, Bell, UserCircle, Calendar as CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const NavLink = ({ to, label, mobileLabel, current, testId }) => (
    <Link to={to} data-testid={testId}
        className={`px-1.5 sm:px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-bold tracking-wide transition border whitespace-nowrap ${current ? "bg-oxford text-white border-oxford" : "border-transparent text-slate-700 hover:bg-bonehover"}`}>
        <span className="sm:hidden">{mobileLabel || label}</span>
        <span className="hidden sm:inline">{label}</span>
    </Link>
);

const NotificationBell = () => {
    const [unread, setUnread] = useState(0);
    useEffect(() => {
        const fetchInbox = () => api.get("/notifications/inbox").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
        fetchInbox();
        const t = setInterval(fetchInbox, 30000);
        return () => clearInterval(t);
    }, []);
    return (
        <Link to="/inbox" data-testid="header-bell" className="relative w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <Bell className="w-4 h-4 text-slate-700" />
            {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-terracotta text-white text-[9px] font-bold flex items-center justify-center px-1">{unread}</span>}
        </Link>
    );
};

const CalendarBadge = () => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        const f = () => api.get("/events/upcoming", { params: { days: 7 } }).then((r) => setCount(r.data.count || 0)).catch(() => {});
        f();
        const t = setInterval(f, 60000);
        return () => clearInterval(t);
    }, []);
    return (
        <Link to="/calendar" data-testid="header-calendar" className="relative w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" title="Kalendari shkollor">
            <CalendarIcon className="w-4 h-4 text-slate-700" />
            {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-sage text-white text-[9px] font-bold flex items-center justify-center px-1">{count}</span>}
        </Link>
    );
};

// Heartbeat: send /api/analytics/heartbeat every 60s while tab is visible.
// Used by admin/director to compute total active minutes per user.
const useAnalyticsHeartbeat = () => {
    useEffect(() => {
        const ping = () => {
            if (document.visibilityState !== "visible") return;
            api.post("/analytics/heartbeat").catch(() => {});
        };
        ping();
        const t = setInterval(ping, 60000);
        const onVisibility = () => { if (document.visibilityState === "visible") ping(); };
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            clearInterval(t);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);
};

const ParentNav = ({ pathname }) => {
    const [studentId, setStudentId] = useState(null);
    useEffect(() => {
        api.get("/parent/children").then((r) => {
            if (r.data.length > 0) setStudentId(r.data[0].id);
        }).catch(() => {});
    }, []);
    if (!studentId) return null;
    const isGrades = pathname.startsWith(`/parent/student/${studentId}`) && !pathname.endsWith("/absences") && !pathname.endsWith("/comments") && !pathname.endsWith("/report");
    const isAbs = pathname.endsWith("/absences");
    const isCom = pathname.endsWith("/comments");

    return (
        <nav className="flex items-center gap-1 sm:gap-1.5">
            <NavLink to={`/parent/student/${studentId}`} label="NOTAT" current={isGrades} testId="header-nav-notat" />
            <NavLink to={`/parent/student/${studentId}/absences`} label="MUNGESAT" current={isAbs} testId="header-nav-mungesat" />
            <NavLink to={`/parent/student/${studentId}/comments`} label="KOMENTET" current={isCom} testId="header-nav-komentet" />
        </nav>
    );
};

const StudentNav = ({ pathname }) => {
    const isGrades = pathname.startsWith("/student/grades");
    const isTT = pathname.startsWith("/student/timetable");
    const isLMS = pathname.startsWith("/student/lms");
    const isAsg = pathname.startsWith("/student/assignments");
    return (
        <nav className="flex items-center gap-1 sm:gap-1.5">
            <NavLink to="/student/grades" label="NOTAT" current={isGrades} testId="header-nav-student-grades" />
            <NavLink to="/student/timetable" label="ORARI" current={isTT} testId="header-nav-student-timetable" />
            <NavLink to="/student/lms" label="LMS" current={isLMS} testId="header-nav-student-lms" />
            <NavLink to="/student/assignments" label="DETYRAT" mobileLabel="DETYR" current={isAsg} testId="header-nav-student-assignments" />
        </nav>
    );
};

const TeacherNav = ({ pathname }) => {
    const [isKujdestar, setIsKujdestar] = useState(false);
    useEffect(() => {
        api.get("/kujdestari/my-classes").then((r) => setIsKujdestar((r.data || []).length > 0)).catch(() => setIsKujdestar(false));
    }, []);
    const isTT = pathname.startsWith("/teacher/timetable");
    const isLMS = pathname.startsWith("/teacher/lms");
    const isNotify = pathname.startsWith("/teacher/notify");
    const isComments = pathname.startsWith("/teacher/comments");
    const isPlans = pathname.startsWith("/teacher/plans");
    const isSP = pathname.startsWith("/teacher/student-plans");
    const isAsg = pathname.startsWith("/teacher/assignments");
    const isCons = pathname.startsWith("/teacher/consultations");
    const isKujd = pathname.startsWith("/kujdestari");
    return (
        <nav className="flex items-center gap-0.5 sm:gap-1 flex-nowrap" data-testid="teacher-header-nav">
            {isKujdestar && (
                <Link to="/kujdestari" data-testid="header-nav-kujdestari"
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-black tracking-tighter transition border shrink-0 ${isKujd ? "bg-oxford text-white border-oxford" : "bg-oxford/10 text-oxford border-oxford/30 hover:bg-oxford/20"}`}
                    title="Kujdestari">
                    K
                </Link>
            )}
            <NavLink to="/teacher/timetable" label="ORARI" current={isTT} testId="header-nav-teacher-timetable" />
            <NavLink to="/teacher/lms" label="LMS" current={isLMS} testId="header-nav-teacher-lms" />
            <NavLink to="/teacher/plans" label="PLANET" mobileLabel="PLAN" current={isPlans} testId="header-nav-teacher-plans" />
            <NavLink to="/teacher/student-plans" label="A/B/C" current={isSP} testId="header-nav-teacher-sp" />
            <NavLink to="/teacher/assignments" label="DETYRAT" mobileLabel="DETYR" current={isAsg} testId="header-nav-teacher-assignments" />
            <NavLink to="/teacher/consultations" label="KONSULTA" mobileLabel="KONS" current={isCons} testId="header-nav-teacher-consultations" />
            <NavLink to="/teacher/notify" label="NJOFTIM" mobileLabel="NJOFTO" current={isNotify} testId="header-nav-teacher-notify" />
            <NavLink to="/teacher/comments" label="KOMENT" current={isComments} testId="header-nav-teacher-comments" />
        </nav>
    );
};

const AdministrataNav = ({ pathname }) => {
    const isH = pathname === "/administrata";
    const isStud = pathname.startsWith("/administrata/students");
    const isAppt = pathname.startsWith("/administrata/appointments");
    const isColl = pathname.startsWith("/drejtoresha/collaborations");
    return (
        <nav className="flex items-center gap-1 sm:gap-1.5" data-testid="administrata-header-nav">
            <NavLink to="/administrata" label="HOME" current={isH} testId="header-nav-adm-home" />
            <NavLink to="/administrata/students" label="NXËNËS" mobileLabel="NXNS" current={isStud} testId="header-nav-adm-students" />
            <NavLink to="/administrata/appointments" label="TAKIME" current={isAppt} testId="header-nav-adm-appts" />
            <NavLink to="/drejtoresha/collaborations" label="PARTNERË" mobileLabel="PARTN" current={isColl} testId="header-nav-adm-collabs" />
        </nav>
    );
};

const SecretaryNav = ({ pathname }) => {
    const isH = pathname === "/secretary";
    const isAppt = pathname.startsWith("/administrata/appointments");
    return (
        <nav className="flex items-center gap-1 sm:gap-1.5" data-testid="secretary-header-nav">
            <NavLink to="/secretary" label="HOME" current={isH} testId="header-nav-sec-home" />
            <NavLink to="/administrata/appointments" label="TAKIME" current={isAppt} testId="header-nav-sec-appts" />
        </nav>
    );
};

const SigurimiNav = ({ pathname }) => {
    const [kindHint, setKindHint] = useState("student");
    useEffect(() => {
        // if on incident page capture kind from url
        const m = pathname.match(/\/sigurimi\/incident\/(student|teacher)\//);
        if (m) setKindHint(m[1]);
    }, [pathname]);
    const isHome = pathname === "/sigurimi" || pathname.startsWith("/sigurimi/incident");
    const isRep = pathname.startsWith("/sigurimi/reports");
    return (
        <nav className="flex items-center gap-1 sm:gap-1.5" data-testid="sigurimi-header-nav">
            <NavLink to="/sigurimi" label="NXËNËS / MËSUES" mobileLabel="PERSONAT" current={isHome} testId="header-nav-sig-people" />
            <NavLink to="/sigurimi/reports" label="RAPORTET" mobileLabel="RAPORT" current={isRep} testId="header-nav-sig-reports" />
        </nav>
    );
};

const DrejtoreshaNav = ({ pathname }) => {
    const isH = pathname === "/drejtoresha";
    const isAppt = pathname.startsWith("/administrata/appointments");
    const isColl = pathname.startsWith("/drejtoresha/collaborations");
    const isSig = pathname.startsWith("/sigurimi/reports");
    return (
        <nav className="flex items-center gap-1 sm:gap-1.5" data-testid="drejtoresha-header-nav">
            <NavLink to="/drejtoresha" label="HOME" current={isH} testId="header-nav-dr-home" />
            <NavLink to="/administrata/appointments" label="TAKIME" current={isAppt} testId="header-nav-dr-appts" />
            <NavLink to="/drejtoresha/collaborations" label="PARTNERË" mobileLabel="PARTN" current={isColl} testId="header-nav-dr-collabs" />
            <NavLink to="/sigurimi/reports" label="SIGURIMI" mobileLabel="SIG" current={isSig} testId="header-nav-dr-sig" />
        </nav>
    );
};

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    useAnalyticsHeartbeat();

    const handleLogout = async () => { await logout(); navigate("/login"); };

    return (
        <div className="min-h-screen paper-bg">
            <header className="bg-white/85 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex items-center gap-2 sm:gap-4">
                    <Link to={user?.role === "super_admin" ? "/admin" : user?.role === "teacher" ? "/teacher" : user?.role === "student" ? "/student" : user?.role === "secretary" ? "/secretary" : user?.role === "quality_coordinator" ? "/quality" : user?.role === "administrata" ? "/administrata" : user?.role === "sigurimi" ? "/sigurimi" : user?.role === "drejtoresha" ? "/drejtoresha" : "/parent"} className="flex items-center gap-2 sm:gap-3 shrink-0" data-testid="header-logo">
                        <img src="https://customer-assets.emergentagent.com/job_parent-report-card-1/artifacts/xuzrd42o_logo2.png" alt="GG Logo"
                            className="w-8 h-8 sm:w-9 sm:h-9 object-contain" />
                        <div className="hidden md:block leading-tight">
                            <div className="font-heading font-bold text-oxford text-base">German Gymnasium</div>
                            <div className="text-[9px] tracking-[0.2em] uppercase text-slate-500">Platformë Notash</div>
                        </div>
                    </Link>

                    {user?.role === "parent" && (
                        <div className="flex-1 flex justify-center">
                            <ParentNav pathname={pathname} />
                        </div>
                    )}
                    {user?.role === "student" && (
                        <div className="flex-1 flex justify-center">
                            <StudentNav pathname={pathname} />
                        </div>
                    )}
                    {user?.role === "teacher" && (
                        <div className="flex-1 flex justify-center">
                            <TeacherNav pathname={pathname} />
                        </div>
                    )}
                    {user?.role === "administrata" && (
                        <div className="flex-1 flex justify-center">
                            <AdministrataNav pathname={pathname} />
                        </div>
                    )}
                    {user?.role === "secretary" && (
                        <div className="flex-1 flex justify-center">
                            <SecretaryNav pathname={pathname} />
                        </div>
                    )}
                    {user?.role === "sigurimi" && (
                        <div className="flex-1 flex justify-center">
                            <SigurimiNav pathname={pathname} />
                        </div>
                    )}
                    {user?.role === "drejtoresha" && (
                        <div className="flex-1 flex justify-center">
                            <DrejtoreshaNav pathname={pathname} />
                        </div>
                    )}
                    {(!user || user?.role === "super_admin" || user?.role === "quality_coordinator") && <div className="flex-1" />}

                    {user && (
                        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                            <CalendarBadge />
                            <NotificationBell />
                            <Link to="/profile" data-testid="header-profile-btn" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" title="Profili im">
                                <UserCircle className="w-5 h-5 text-slate-700" />
                            </Link>
                            <div className="text-right hidden lg:block">
                                <div className="text-sm font-semibold text-slate-800" data-testid="header-user-name">{user.name}</div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                    {user.role === "parent" ? "Prind" : user.role === "super_admin" ? "Super Admin" : user.role === "student" ? "Nxënës" : user.role === "secretary" ? "Sekretar" : user.role === "quality_coordinator" ? "Koordinator" : user.role === "administrata" ? "Administratë" : user.role === "sigurimi" ? "Sigurimi" : user.role === "drejtoresha" ? "Drejtoresha" : "Mësues"}
                                </div>
                            </div>
                            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="header-logout-btn" className="gap-1.5 px-2 sm:px-3">
                                <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Dil</span>
                            </Button>
                        </div>
                    )}
                </div>
            </header>
            <main className="max-w-7xl mx-auto px-3 sm:px-6 pt-6 sm:pt-8 pb-8 animate-fade-up">{children}</main>
        </div>
    );
};
